import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { supabase } from '../lib/supabaseClient';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { AnalyticsEvents, setAnalyticsUser, clearAnalyticsUser } from '../lib/analytics';
import { signupAttribution } from '../lib/attribution';
import { useTenantStore } from './tenant';

// Backstop for a supabase-js bug (@supabase/auth-js ≤ 2.67.x): when the
// server responds to /logout with `session_not_found` (status 403, which
// happens whenever the session has already been revoked elsewhere), the
// error is thrown as AuthSessionMissingError. _signOut only swallows
// AuthApiError 401/403/404 — it does not match this class — so it returns
// early and skips _removeSession(). The JWT stays in localStorage, the next
// page load re-reads it, and the user appears signed in until the access
// token expires. We must clear the storage ourselves to break that loop.
function clearLocalAuthStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch (err) {
    console.warn('[Auth] clearLocalAuthStorage failed:', err);
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const initialized = ref(false);

  // Organization and role tracking
  const currentOrganization = ref(null);
  const userRole = ref(null);
  const betaEnabled = ref(false);
  const organizationLoading = ref(false);

  // Surfaced to WorkspaceNotFound when the signed-in user's org doesn't match
  // the tenant subdomain they hit. Set right before forcing a sign-out.
  // tenantAccessError carries the human-readable message; tenantAccessDenied
  // is the boolean the router guard uses to short-circuit navigation.
  const tenantAccessError = ref(null);
  const tenantAccessDenied = ref(false);

  // Subscription tracking
  const subscription = ref(null);

  // Computed properties
  const organizationId = computed(() => currentOrganization.value?.id);
  const organizationName = computed(() => currentOrganization.value?.name);
  const isAuthenticated = computed(() => !!user.value);
  const hasOrganization = computed(() => !!currentOrganization.value);
  const currentPlan = computed(() => subscription.value?.plan_id || 'free');
  const isFreeTier = computed(() => currentPlan.value === 'free');
  const isPaidTier = computed(() => !isFreeTier.value);

  async function signup(email, password) {
    try {
      loading.value = true;
      
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password: password.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            email: email.trim(),
            signup_attribution: signupAttribution()
          }
        }
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          throw new Error('This email is already registered. Please try logging in instead.');
        }
        throw signUpError;
      }

      if (!data?.user) {
        throw new Error('Signup failed. Please try again.');
      }

      // Supabase returns "success" for already-registered emails too
      // (enumeration protection): a confirmed existing email yields a fake
      // user with an empty identities array; an unconfirmed existing email
      // yields the original (old) user and just re-sends the confirmation.
      // Only a fresh identities entry + a just-now created_at means a new
      // account actually got created — callers use this to avoid firing
      // the sign_up analytics event on repeat attempts.
      const isNewUser =
        (data.user.identities?.length ?? 0) > 0 &&
        Date.now() - new Date(data.user.created_at).getTime() < 60_000;

      return { user: data.user, isNewUser };
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      } else if (err.message?.includes('User already registered')) {
        throw new Error('This email is already registered. Please try logging in instead.');
      } else {
        throw new Error(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      loading.value = false;
    }
  }

  // Cache the in-flight initialize() promise so concurrent callers (main.js
  // and the router's beforeEach guard both race to call this on startup)
  // share a single run. Without this, two onAuthStateChange listeners get
  // registered and every auth event fires twice.
  let initPromise = null;

  async function initialize() {
    if (initialized.value) return;
    if (initPromise) return initPromise;
    initPromise = _initialize();
    return initPromise;
  }

  async function _initialize() {
    try {
      loading.value = true;
      error.value = null;

      let session = null;
      try {
        const { data } = await supabase.auth.getSession();
        session = data.session;
      } catch (lockErr) {
        // A NavigatorLockAcquireTimeoutError means another tab or an in-flight
        // Supabase operation already holds the auth lock (common when a
        // password-reset email link opens a new tab while the app is already
        // running). Treat as unauthenticated for this navigation; the
        // onAuthStateChange listener below will reconcile state once the lock
        // is released.
        if (lockErr.name === 'NavigatorLockAcquireTimeoutError') {
          console.warn('[Auth] Navigator lock timeout during initialize; proceeding unauthenticated');
        } else {
          throw lockErr;
        }
      }

      if (session) {
        user.value = session.user;
        // Fetch organization info after getting session
        await fetchUserOrganization();
      }

      // Subscribe to auth state changes (token refresh, sign out, etc.)
      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('[Auth] Auth state changed:', event, session?.user?.id);
        
        if (event === 'SIGNED_OUT' || !session) {
          // User was signed out (token expired, manual logout, etc.)
          user.value = null;
          currentOrganization.value = null;
          userRole.value = null;
          betaEnabled.value = false;
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY') {
          // User signed in, token refreshed, or recovery session established
          user.value = session.user;
          
          // Only fetch organization if we don't have it yet (avoid duplicate calls)
          if (!currentOrganization.value) {
            await fetchUserOrganization();
          }
        } else if (event === 'USER_UPDATED') {
          // User profile was updated
          user.value = session.user;
        }
      });
    } catch (err) {
      console.error('Error initializing auth:', err);
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function fetchUserOrganization() {
    if (!user.value) {
      currentOrganization.value = null;
      userRole.value = null;
      return;
    }

    try {
      organizationLoading.value = true;
      console.log('[Auth] Fetching organization for user:', user.value.id);

      const { data, error: orgError } = await supabase
        .from('user_organizations')
        .select(`
          organization_id,
          role,
          beta_enabled,
          organizations (
            id,
            name,
            settings,
            subdomain
          )
        `)
        .eq('user_id', user.value.id)
        .single();

      console.log('[Auth] user_organizations query result:', { data, error: orgError });

      if (orgError) {
        // User might not have an organization yet (new signup)
        if (orgError.code === 'PGRST116') {
          console.log('[Auth] User is not part of any organization');
          currentOrganization.value = null;
          userRole.value = null;
          betaEnabled.value = false;
          // A user with no org on a tenant subdomain shouldn't be funneled into
          // /onboarding — they don't belong to that workspace at all.
          await enforceTenantGate();
          return;
        }
        throw orgError;
      }

      if (data) {
        currentOrganization.value = data.organizations;
        userRole.value = data.role;
        betaEnabled.value = data.beta_enabled || false;
        console.log('[Auth] Organization loaded:', {
          org: currentOrganization.value,
          role: userRole.value,
          betaEnabled: betaEnabled.value
        });

        // Enforce subdomain tenant gate: the user's org must match the
        // tenant implied by the URL. If not, sign them out and surface a
        // message on the login page. This runs before subscription/Sentry
        // wiring so we don't leak any per-org data into a rejected session.
        if (await enforceTenantGate()) {
          return;
        }

        // Fetch subscription data
        if (currentOrganization.value?.id) {
          await fetchSubscription(currentOrganization.value.id);
        }

        // Set Sentry user context for error attribution
        if (user.value) {
          setAnalyticsUser(user.value.id);
          setSentryUser({
            id: user.value.id,
            email: user.value.email,
            organizationId: currentOrganization.value?.id,
            role: userRole.value,
          });
        }
      }
    } catch (err) {
      console.error('[Auth] Error fetching user organization:', err);
      currentOrganization.value = null;
      userRole.value = null;
      betaEnabled.value = false;
      subscription.value = null;
    } finally {
      organizationLoading.value = false;
    }
  }

  // Returns true if the gate rejected the session (caller should bail).
  // No-op when the URL is not tenant-gated. Rejects in two cases:
  //   1. The user's loaded org doesn't match the tenant org (cross-tenant).
  //   2. The user has no org at all (onboarding-incomplete or orphaned user
  //      who shouldn't be sent to /onboarding on someone else's subdomain).
  async function enforceTenantGate() {
    const tenantStore = useTenantStore();
    if (!tenantStore.isTenantGated) return false;

    const tenantOrgId = tenantStore.org?.id;
    if (!tenantOrgId) return false; // shouldn't happen when isTenantGated, but safe

    const userOrgId = currentOrganization.value?.id;
    if (userOrgId && userOrgId === tenantOrgId) return false;

    const tenantName = tenantStore.org?.name || tenantStore.slug;
    console.warn('[Auth] Tenant access denied: user org', userOrgId || '(none)', 'does not match tenant', tenantOrgId);
    tenantAccessError.value = userOrgId
      ? `Your account doesn't have access to the ${tenantName} workspace.`
      : `Your account isn't a member of the ${tenantName} workspace.`;
    tenantAccessDenied.value = true;
    try {
      // Local scope only — we just want to clear THIS browser's session for
      // the wrong tenant. Default 'global' would also revoke the user's
      // sessions in other tabs/devices/origins (e.g. their valid session on
      // dev.event-flow.co), leaving stale-but-present JWTs that 401 on every
      // call until they hard-refresh.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      console.warn('[Auth] signOut during tenant rejection failed:', err);
    }
    clearLocalAuthStorage();
    user.value = null;
    currentOrganization.value = null;
    userRole.value = null;
    betaEnabled.value = false;
    subscription.value = null;
    clearSentryUser();
    clearAnalyticsUser();
    return true;
  }

  async function fetchSubscription(orgId) {
    try {
      const { data, error: subError } = await supabase
        .from('organization_subscriptions')
        .select(`
          id,
          plan_id,
          status,
          billing_interval,
          current_period_start,
          current_period_end,
          cancel_at_period_end,
          stripe_customer_id,
          stripe_subscription_id
        `)
        .eq('organization_id', orgId)
        .neq('status', 'canceled')
        .maybeSingle();

      if (subError) {
        console.warn('[Auth] Error fetching subscription:', subError);
      }

      subscription.value = data || null;
      console.log('[Auth] Subscription loaded:', subscription.value?.plan_id || 'none');
    } catch (err) {
      console.warn('[Auth] Subscription fetch error:', err);
      subscription.value = null;
    }
  }

  async function login(email, password) {
    try {
      loading.value = true;
      error.value = null;

      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });

      if (signInError) {
        throw new Error(signInError.message);
      }
      
      if (!data?.user) {
        throw new Error('Login failed. Please try again.');
      }

      user.value = data.user;
      
      // Fetch organization info after login
      await fetchUserOrganization();

      // Track login event
      AnalyticsEvents.login();
      
      return data;
    } catch (err) {
      if (err.message?.includes('Failed to fetch') || err.code === 'NETWORK_ERROR') {
        throw new Error('Unable to connect to the server. Please try again in a few moments.');
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      loading.value = true;

      // Use scope: 'local' — clears localStorage and fires SIGNED_OUT, but
      // does NOT make a server call to revoke. The server call is what fails
      // with AuthSessionMissingError / 403 when the JWT is already invalid,
      // and a failure there previously left localStorage populated, causing
      // a redirect loop after the next page load re-read the stale token.
      // Side effect: clicking Sign Out only logs out this browser, not other
      // devices. That's the standard SaaS expectation; a separate "sign out
      // everywhere" action can be added later if needed.
      try {
        const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
        if (signOutError) {
          console.warn('[Auth] supabase.signOut returned an error (clearing local state anyway):', signOutError);
        }
      } catch (signOutErr) {
        console.warn('[Auth] supabase.signOut threw (clearing local state anyway):', signOutErr);
      }

      clearLocalAuthStorage();

      user.value = null;
      currentOrganization.value = null;
      userRole.value = null;
      betaEnabled.value = false;
      subscription.value = null;

      clearSentryUser();
      clearAnalyticsUser();
      AnalyticsEvents.logout();
    } finally {
      loading.value = false;
    }
  }

  async function changePassword(newPassword) {
    try {
      loading.value = true;
      error.value = null;

      if (!newPassword || newPassword.length < 12) {
        throw new Error('Password must be at least 12 characters long');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword.trim()
      });

      if (updateError) {
        throw new Error(updateError.message);
      }

      return { success: true };
    } catch (err) {
      if (err.message?.includes('Failed to fetch') || err.code === 'NETWORK_ERROR') {
        throw new Error('Unable to connect to the server. Please try again in a few moments.');
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Update user profile (display name, etc.)
  async function updateProfile({ displayName }) {
    if (!user.value) {
      throw new Error('User must be logged in to update profile');
    }

    try {
      loading.value = true;
      error.value = null;

      const { data, error: updateError } = await supabase.auth.updateUser({
        data: {
          display_name: displayName?.trim() || null
        }
      });

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Update local user ref with new metadata
      if (data?.user) {
        user.value = data.user;
      }

      return { success: true };
    } catch (err) {
      if (err.message?.includes('Failed to fetch') || err.code === 'NETWORK_ERROR') {
        throw new Error('Unable to connect to the server. Please try again in a few moments.');
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Send a passwordless sign-in link (triggers the magic_link.html template).
  // Set shouldCreateUser: false to avoid silently creating accounts for typos.
  async function sendMagicLink(email) {
    try {
      loading.value = true;
      error.value = null;

      if (!email) {
        throw new Error('Email is required');
      }

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: false,
        },
      });

      if (otpError) throw otpError;

      return { success: true };
    } catch (err) {
      if (err.message?.includes('Failed to fetch') || err.code === 'NETWORK_ERROR') {
        throw new Error('Unable to connect to the server. Please try again in a few moments.');
      }
      throw new Error(err.message || 'Failed to send sign-in link');
    } finally {
      loading.value = false;
    }
  }

  // Request a change to the signed-in user's email address. Supabase Auth
  // requires confirmation on the new email (and, if mailer_secure_email_change
  // is enabled, also on the current one) — both triggered by email_change.html.
  async function changeEmail(newEmail) {
    if (!user.value) {
      throw new Error('You must be signed in to change your email');
    }

    try {
      loading.value = true;
      error.value = null;

      const trimmed = newEmail?.trim();
      if (!trimmed) {
        throw new Error('New email address is required');
      }
      if (trimmed.toLowerCase() === user.value.email?.toLowerCase()) {
        throw new Error('This is already your email address');
      }

      const { error: updateError } = await supabase.auth.updateUser(
        { email: trimmed },
        { emailRedirectTo: `${window.location.origin}/auth/callback` }
      );

      if (updateError) throw updateError;

      return { success: true };
    } catch (err) {
      if (err.message?.includes('Failed to fetch') || err.code === 'NETWORK_ERROR') {
        throw new Error('Unable to connect to the server. Please try again in a few moments.');
      }
      throw new Error(err.message || 'Failed to start email change');
    } finally {
      loading.value = false;
    }
  }

  async function forgotPassword(email) {
    try {
      loading.value = true;
      error.value = null;

      // No ?type=recovery here — the recovery.html template builds the link as
      // {{ .RedirectTo }}?token_hash=...&type=recovery, so a query string on
      // redirectTo would produce a malformed double-"?" URL.
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (resetError) throw resetError;

      return { success: true };
    } catch (err) {
      throw new Error(err.message || 'Failed to send reset email');
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(newPassword) {
    try {
      loading.value = true;
      error.value = null;

      if (!newPassword || newPassword.length < 12) {
        throw new Error('Password must be at least 12 characters long');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword.trim(),
      });

      if (updateError) throw updateError;

      return { success: true };
    } catch (err) {
      throw new Error(err.message || 'Failed to reset password');
    } finally {
      loading.value = false;
    }
  }

  // Sync auth store state from an already-established Supabase session.
  // Used by AuthCallback to avoid creating duplicate onAuthStateChange listeners.
  async function syncFromSession() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('[Auth] syncFromSession: no active session found');
        return false;
      }
      user.value = session.user;
      await fetchUserOrganization();
      return true;
    } catch (err) {
      console.error('[Auth] syncFromSession error:', err);
      return false;
    }
  }

  // Create organization for new user (used during signup flow)
  async function createOrganization(name) {
    if (!user.value) {
      throw new Error('User must be logged in to create an organization');
    }

    console.log('[Auth] createOrganization — user id:', user.value.id, '| name:', name);

    try {
      loading.value = true;

      // Generate the org ID client-side so we can insert both rows without
      // using INSERT...RETURNING. The RETURNING clause checks the SELECT RLS
      // policy before the membership row exists, which would always fail.
      const orgId = crypto.randomUUID();

      const { error: createError } = await supabase
        .from('organizations')
        .insert({ id: orgId, name });

      if (createError) {
        console.error('[Auth] createOrganization INSERT organizations failed:', createError);
        throw createError;
      }

      console.log('[Auth] Organization row inserted:', orgId);

      // Add user as administrator — membership must exist before we SELECT the org
      const { error: memberError } = await supabase
        .from('user_organizations')
        .insert({
          user_id: user.value.id,
          organization_id: orgId,
          role: 'administrator'
        });

      if (memberError) {
        console.error('[Auth] createOrganization INSERT user_organizations failed:', memberError);
        throw memberError;
      }

      // Now that membership exists the SELECT RLS policy will pass
      const { data: org, error: fetchError } = await supabase
        .from('organizations')
        .select('id, name, settings, subdomain')
        .eq('id', orgId)
        .single();

      if (fetchError) {
        console.error('[Auth] createOrganization SELECT organizations failed:', fetchError);
        throw fetchError;
      }

      // Update local state
      currentOrganization.value = org;
      userRole.value = 'administrator';

      return org;
    } catch (err) {
      console.error('[Auth] createOrganization error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    user,
    loading,
    error,
    initialized,
    currentOrganization,
    userRole,
    betaEnabled,
    organizationLoading,
    subscription,
    tenantAccessError,
    tenantAccessDenied,

    // Computed
    organizationId,
    organizationName,
    isAuthenticated,
    hasOrganization,
    currentPlan,
    isFreeTier,
    isPaidTier,
    
    // Methods
    initialize,
    syncFromSession,
    login,
    logout,
    signup,
    changePassword,
    updateProfile,
    sendMagicLink,
    changeEmail,
    forgotPassword,
    resetPassword,
    fetchUserOrganization,
    fetchSubscription,
    createOrganization,
  };
});
